import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import { useRealmApp } from "../RealmApp";
import { useDatesContext } from "context/DatesContext";

const useItems = () => {
  const app = useRealmApp();
  const { items, loading } = useAllItems(app.currentUser.id);
  return {
    loading,
    items,
  };
};

export default useItems;

export const useStockMasters = (storeId) => {
  const app = useRealmApp();
  const { stockMasters, loading } = useAllStockMasters(
    app.currentUser.id,
    storeId
  );
  return {
    loading,
    stockMasters,
  };
};

function useAllItems(ownerId) {
  const { getCacheControl } = useDatesContext();
  const fetchPolicy = getCacheControl("useAllItems", 60 * 1000);
  const { data, loading, error } = useQuery(
    gql`
      query getItems($ownerId: String!) {
        items(query: { owner_id: $ownerId }, limit: 0) {
          _id
          sku
          name
          invoiceName
          description
          gtin
          type
          barcode
          price
          tax1
          tax2
          tax3
          subcategoryId
          tags
          color
          image
          stockMode
          minStock
          favorite
          pinToStart
          alwaysAskPrice
          includesCommission
          disabled
          hidden

          useScale
          variantId
          measureUnit
          variantConfig {
            pricePerVariant
            variants {
              variant
              price
              priceListId
            }
          }
          modifierConfig {
            _id
            excludeList
          }
          mixAndMatch {
            steps {
              name
              items {
                items
                quantity
                price
              }
            }
          }
          updateDatetime
          creationDatetime
        }
      }
    `,
    { variables: { ownerId }, fetchPolicy }
  );
  if (error) {
    throw new Error(`Failed to fetch tasks: ${error.message}`);
  }
  // If the query has finished, return the tasks from the result data
  // Otherwise, return an empty list
  const items = data?.items ?? [];
  return { items, loading };
}

function useAllStockMasters(ownerId, storeId) {
  const { data, loading, error } = useQuery(
    gql`
      query getStockMasters($ownerId: String!, $storeId: ObjectId) {
        stockMasters(
          query: { owner_id: $ownerId, storeId: $storeId }
          limit: 0
        ) {
          _id
          ownerId
          storeId
          barcode
          quantity
          minStock
          updateDatetime
          creationDatetime
        }
      }
    `,
    { variables: { ownerId, storeId: storeId || null } }
  );
  if (error) {
    throw new Error(`Failed to fetch tasks: ${error.message}`);
  }
  // If the query has finished, return the tasks from the result data
  // Otherwise, return an empty list
  const stockMasters = data?.stockMasters ?? [];
  return { stockMasters, loading };
}
